import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Tab, Tabs, Typography} from '@mui/material'
import Filters, {FilterResult} from 'src/components-new/data/Filters'
import EmptyState from 'src/components-new/layout/EmptyState'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import DynamicTherapyForm, {TherapyDataSchemaElement} from 'src/pages-new/patient/tabs/therapy/DynamicTherapyForm'
import SecondaryTherapyDetail from 'src/pages-new/patient/tabs/therapy/SecondaryTherapyDetail'
import {formatTherapyState} from 'src/pages-new/patient/tabs/therapy/therapyFormatters'
import {
  applySecondaryTherapyFilters,
  buildSecondaryTherapyFilterItems,
  getSecondaryTherapyDrugName,
  getSecondaryTherapyState,
  SecondaryTherapyFilterConfig,
  sortSecondaryTherapies
} from 'src/pages-new/patient/tabs/therapy/secondaryTherapyFilters'
import {SecondaryTherapyDataSchemaElement, resolveSecondaryTherapyDataSchema} from 'src/constants/secondaryTherapyFields'

interface TherapyTabProps {
  patientId: string
}

type MainSubTab = 'therapies' | 'secondaryTherapies'
type TherapyStatus = 'ACTIVE' | 'FUTURE' | 'PAST' | 'UNKNOWN'

// Ported from getTherapyStatus / the therapies.sort(...) block in
// src/components/templates/PatientDetailsComponent/TherapyCard/TherapyCard.tsx (~L50-72), using
// plain Date comparisons instead of moment (moment is legacy-only; new-style pages avoid it).
function getTherapyStatus(therapy: Record<string, any>): TherapyStatus {
  const today = new Date()
  const startDate = new Date(therapy.startDate)
  const endDate = therapy.endDate ? new Date(therapy.endDate) : null
  const isCurrentlyBetween = Boolean(endDate) && today >= startDate && today <= (endDate as Date)

  if (isCurrentlyBetween || therapy.state === 'ACTIVE') return 'ACTIVE'
  if (endDate && today > endDate) return 'PAST'
  if (today < startDate) return 'FUTURE'
  return 'UNKNOWN'
}

// NOTE: this mirrors the legacy statusOrder literal exactly (FUTURE, ACTIVE, PAST) even though it reads
// counter-intuitively — the legacy UI always force-selects the first ACTIVE therapy separately, so the
// sort order only affects the order of the tab strip, not which therapy opens by default.
const STATUS_SORT_ORDER: Record<TherapyStatus, number> = {FUTURE: 0, ACTIVE: 1, PAST: 2, UNKNOWN: 3}

function sortTherapiesByStatus(therapies: Record<string, any>[]): Record<string, any>[] {
  return [...therapies].sort((a, b) => {
    const statusA = getTherapyStatus(a)
    const statusB = getTherapyStatus(b)
    const diff = STATUS_SORT_ORDER[statusA] - STATUS_SORT_ORDER[statusB]
    if (diff !== 0) return diff

    const dateA = new Date(a.startDate).getTime()
    const dateB = new Date(b.startDate).getTime()
    return statusA === 'FUTURE' || statusA === 'ACTIVE' ? dateA - dateB : dateB - dateA
  })
}

function getTherapyStatusPillProps(status: TherapyStatus): {label: string; color: 'success' | 'info' | 'error' | 'default'} {
  if (status === 'ACTIVE') return {label: 'Attiva', color: 'success'}
  if (status === 'FUTURE') return {label: 'Futura', color: 'info'}
  if (status === 'PAST') return {label: 'Precedente', color: 'error'}
  return {label: 'N/D', color: 'default'}
}

export default function TherapyTab({patientId}: TherapyTabProps): JSX.Element {
  const toast = useToast()
  const therapiesConfiguration = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.therapiesConfiguration
  )
  const tabsConfig = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.tabs
  )
  const userGroup = useAppSelector(state => state.userReducer.data?.userGroup)
  const editable = Boolean(userGroup?.toUpperCase().includes('DOCTOR'))
  const dataSchema: TherapyDataSchemaElement[] = therapiesConfiguration?.dataSchema ?? []
  const secondaryTherapiesConfiguration = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.secondaryTherapiesConfiguration
  )
  const secondaryTherapiesDataSchema: SecondaryTherapyDataSchemaElement[] = resolveSecondaryTherapyDataSchema(secondaryTherapiesConfiguration) ?? []

  // "secondaryTherapy" è un toggle opt-out: prima che esistesse questo campo la scheda "Terapie
  // accessorie" era sempre visibile, quindi in assenza del campo (programmi non ancora
  // riconfigurati) resta visibile di default.
  const showPrimaryTherapies = Boolean(tabsConfig?.therapy?.show)
  const showSecondaryTherapies = tabsConfig?.secondaryTherapy?.show !== false

  const [mainTab, setMainTab] = useState<MainSubTab>(showPrimaryTherapies ? 'therapies' : 'secondaryTherapies')

  const [therapies, setTherapies] = useState<Record<string, any>[]>([])
  const [selectedTherapyId, setSelectedTherapyId] = useState<string | undefined>(undefined)
  const [statusTherapies, setStatusTherapies] = useState<string>(STATUS.PENDING)

  const [secondaryTherapies, setSecondaryTherapies] = useState<Record<string, any>[]>([])
  const [statusSecondaryTherapies, setStatusSecondaryTherapies] = useState<string>(STATUS.PENDING)
  const [selectedSecondaryTherapyId, setSelectedSecondaryTherapyId] = useState<string | undefined>(undefined)
  const [secondaryTherapiesFilters, setSecondaryTherapiesFilters] = useState<FilterResult[]>([])

  const fetchTherapies = useCallback(() => {
    apiClient.getPatientsTherapies(patientId, setStatusTherapies)
      .then((data: any) => {
        if (!Array.isArray(data)) return
        const sorted = sortTherapiesByStatus(data)
        setTherapies(sorted)
        setSelectedTherapyId(prevId => {
          if (prevId && sorted.some(t => t._id === prevId)) return prevId
          const activeTherapy = sorted.find(t => getTherapyStatus(t) === 'ACTIVE')
          return (activeTherapy ?? sorted[0])?._id
        })
      })
      .catch(() => setStatusTherapies(STATUS.REJECTED))
  }, [patientId])

  useEffect(() => {
    fetchTherapies()
  }, [fetchTherapies])

  useEffect(() => {
    apiClient.getPatientsSecondaryTherapies(patientId, setStatusSecondaryTherapies)
      .then((data: any) => {
        if (Array.isArray(data)) setSecondaryTherapies(sortSecondaryTherapies(data))
      })
      .catch(() => setStatusSecondaryTherapies(STATUS.REJECTED))
  }, [patientId])

  const selectedTherapy = therapies.find(t => t._id === selectedTherapyId)

  const secondaryTherapiesFiltersConfig: SecondaryTherapyFilterConfig[] | undefined = secondaryTherapiesConfiguration?.filtersConfig
  const secondaryTherapiesFilterItems = useMemo(
    () => buildSecondaryTherapyFilterItems(secondaryTherapiesFiltersConfig ?? [], secondaryTherapies),
    [secondaryTherapiesFiltersConfig, secondaryTherapies]
  )
  const filteredSecondaryTherapies = useMemo(
    () => applySecondaryTherapyFilters(secondaryTherapies, secondaryTherapiesFilters),
    [secondaryTherapies, secondaryTherapiesFilters]
  )
  // When the selected therapy is filtered out, fall back to the first one still visible (active
  // therapies come first, see sortSecondaryTherapies).
  const selectedSecondaryTherapy = filteredSecondaryTherapies.find(t => t._id === selectedSecondaryTherapyId) ??
    filteredSecondaryTherapies[0]

  const handleSaveTherapy = async (patch: Record<string, any>) => {
    if (!selectedTherapy) return
    let patchResult = STATUS.PENDING
    await apiClient.patchPatientTherapy(selectedTherapy._id, patch, (s: string) => { patchResult = s })

    if (patchResult === STATUS.REJECTED) {
      toast.error('Errore nel salvataggio della terapia.')
      return
    }
    toast.success('Terapia aggiornata con successo.')
    fetchTherapies()
  }

  return (
    <Box>
      <Tabs
        value={mainTab}
        onChange={(_e, val: MainSubTab) => setMainTab(val)}
        sx={{
          mb: 3,
          '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
          '& .MuiTabs-indicator': {height: 2}
        }}
      >
        {showPrimaryTherapies && <Tab value="therapies" label="Terapie" />}
        {showSecondaryTherapies && <Tab value="secondaryTherapies" label="Altre terapie" />}
      </Tabs>

      {mainTab === 'therapies' && (
        <Box>
          {statusTherapies === STATUS.PENDING && (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
              <CircularProgress />
            </Box>
          )}

          {statusTherapies === STATUS.REJECTED && (
            <Box sx={{py: 4, textAlign: 'center'}}>
              <Typography variant="body2" color="error">Impossibile caricare le terapie.</Typography>
            </Box>
          )}

          {statusTherapies === STATUS.FULFILLED && therapies.length === 0 && (
            <EmptyState title="Nessuna terapia trovata" description="Non ci sono terapie registrate per questo paziente." />
          )}

          {statusTherapies === STATUS.FULFILLED && therapies.length > 0 && selectedTherapyId && (
            <>
              <Tabs
                value={selectedTherapyId}
                onChange={(_e, val: string) => setSelectedTherapyId(val)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
                  '& .MuiTabs-indicator': {height: 2}
                }}
              >
                {therapies.map(therapy => {
                  const pillProps = getTherapyStatusPillProps(getTherapyStatus(therapy))
                  return (
                    <Tab
                      key={therapy._id}
                      value={therapy._id}
                      label={(
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                          <span>{therapy.name}</span>
                          <StatusPill label={pillProps.label} color={pillProps.color} withDot={false} />
                        </Box>
                      )}
                    />
                  )
                })}
              </Tabs>

              {selectedTherapy && (
                <DynamicTherapyForm
                  therapy={selectedTherapy}
                  dataSchema={dataSchema}
                  editable={editable}
                  onSave={handleSaveTherapy}
                />
              )}
            </>
          )}
        </Box>
      )}

      {mainTab === 'secondaryTherapies' && (
        <Box>
          {statusSecondaryTherapies === STATUS.PENDING && (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
              <CircularProgress />
            </Box>
          )}

          {statusSecondaryTherapies === STATUS.REJECTED && (
            <Box sx={{py: 4, textAlign: 'center'}}>
              <Typography variant="body2" color="error">Impossibile caricare le altre terapie.</Typography>
            </Box>
          )}

          {statusSecondaryTherapies === STATUS.FULFILLED && secondaryTherapies.length === 0 && (
            <EmptyState title="Nessuna terapia accessoria trovata" description="Non ci sono altre terapie registrate per questo paziente." />
          )}

          {statusSecondaryTherapies === STATUS.FULFILLED && secondaryTherapies.length > 0 && (
            <>
              {secondaryTherapiesFilterItems.length > 0 && (
                <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
                  <Filters config={secondaryTherapiesFilterItems} onSubmit={setSecondaryTherapiesFilters} />
                </Box>
              )}

              {filteredSecondaryTherapies.length === 0 && (
                <EmptyState title="Nessuna terapia accessoria trovata" description="Nessuna terapia accessoria corrisponde ai filtri selezionati." />
              )}

              {selectedSecondaryTherapy && (
                <>
                  <Tabs
                    value={selectedSecondaryTherapy._id}
                    onChange={(_e, val: string) => setSelectedSecondaryTherapyId(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      mb: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
                      '& .MuiTabs-indicator': {height: 2}
                    }}
                  >
                    {filteredSecondaryTherapies.map(secondaryTherapy => {
                      const state = getSecondaryTherapyState(secondaryTherapy)
                      return (
                        <Tab
                          key={secondaryTherapy._id}
                          value={secondaryTherapy._id}
                          label={(
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                              <span>{getSecondaryTherapyDrugName(secondaryTherapy)}</span>
                              {state && (
                                <StatusPill
                                  label={formatTherapyState(state)}
                                  color={state === 'NOT_ACTIVE' ? 'error' : 'success'}
                                  withDot={false}
                                />
                              )}
                            </Box>
                          )}
                        />
                      )
                    })}
                  </Tabs>

                  <SecondaryTherapyDetail
                    key={selectedSecondaryTherapy._id}
                    secondaryTherapy={selectedSecondaryTherapy}
                    dataSchema={secondaryTherapiesDataSchema}
                    layout="sections"
                  />
                </>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  )
}
